/**
 * Dashboard Builder
 * 
 * A Cometly-style customizable dashboard with:
 * - Drag & drop section rearrangement
 * - Resize sections from bottom-right corner
 * - Minimize/remove sections
 * - Save flow with dirty state tracking
 * - Dashboard tabs for saved layouts
 * - Backend persistence
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import GridLayout from 'react-grid-layout';
import axios from 'axios';
import { 
  GripHorizontal, Minimize2, Maximize2, X, Save, RotateCcw, 
  Plus, AlertCircle, Loader2, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogDescription, DialogFooter 
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

// Import section components
import DashboardCalendarWidget from '../DashboardCalendarWidget';
import WhatsComingOnlinePanel from '../WhatsComingOnlinePanel';
import { FivePillarsWidget } from '../widgets/FivePillarsWidget';
import { FivePillarsOverviewWidget } from '../widgets/FivePillarsOverviewWidget';
import { AdminUserActivityWidget } from '../widgets/AdminUserActivityWidget';
import { AdminChatWidget } from '../widgets/AdminChatWidget';
import TargetsPacingSection from '../marketing/TargetsPacingSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Widget categories for organization
const WIDGET_CATEGORIES = {
  admin: { label: 'Admin Tools', icon: '🔧' },
  governance: { label: 'Governance', icon: '🏛️' },
  metrics: { label: 'Metrics & KPIs', icon: '📊' },
  marketing: { label: 'Marketing', icon: '📈' },
  activity: { label: 'Activity & Events', icon: '📅' },
  general: { label: 'General', icon: '📌' }
};

// Section registry - maps section_id to component and config
const SECTION_REGISTRY = {
  // Admin-only widgets
  admin_user_activity: {
    component: AdminUserActivitySection,
    title: 'User Activity',
    description: 'Online status, recent logins, and activity feed',
    category: 'admin',
    adminOnly: true,
    minW: 3, minH: 4, maxH: 8,
    defaultW: 4, defaultH: 5
  },
  admin_chat: {
    component: AdminChatSection,
    title: 'Chat Messages',
    description: 'View and reply to messages across all channels',
    category: 'admin',
    adminOnly: true,
    minW: 4, minH: 4, maxH: 8,
    defaultW: 6, defaultH: 5
  },
  welcome_banner: {
    component: WelcomeBannerSection,
    title: 'Welcome Banner',
    description: 'Personalized greeting and quick overview',
    category: 'general',
    minW: 6, minH: 1, maxH: 2,
    defaultW: 12, defaultH: 1
  },
  key_metrics: {
    component: KeyMetricsSection,
    title: 'Key Metrics',
    description: 'Your key performance indicators at a glance',
    category: 'metrics',
    minW: 6, minH: 2, maxH: 4,
    defaultW: 12, defaultH: 2
  },
  upcoming_events: {
    component: UpcomingEventsSection,
    title: 'Upcoming Events',
    description: 'Calendar events and scheduled activities',
    category: 'activity',
    minW: 3, minH: 3, maxH: 6,
    defaultW: 4, defaultH: 3
  },
  recent_activity: {
    component: RecentActivitySection,
    title: 'Recent Activity',
    description: 'Latest updates and notifications',
    category: 'activity',
    minW: 3, minH: 3, maxH: 6,
    defaultW: 4, defaultH: 3
  },
  whats_coming_online: {
    component: WhatsComingOnlineSection,
    title: "What's Coming Online",
    description: 'Upcoming features and releases',
    category: 'general',
    minW: 3, minH: 3, maxH: 6,
    defaultW: 4, defaultH: 3
  },
  five_pillars_overview: {
    component: FivePillarsOverviewSection,
    title: "5 Pillars Overview",
    description: 'MVP progress, pillar metrics, and quick navigation to the 5 Pillars Hub',
    category: 'governance',
    minW: 6, minH: 3, maxH: 5,
    defaultW: 12, defaultH: 3,
    restrictedToEmails: ['ryan@rhinoshieldflorida.com', 'gabe@rhinoshieldflorida.com', 'rmarch@rhinoshield.com']
  },
  development_pillars: {
    component: DevelopmentPillarsSection,
    title: "Development Pillars",
    description: 'Scope, Timeline, Implementation, Data & Security, Cost tracking',
    category: 'governance',
    minW: 6, minH: 3, maxH: 5,
    defaultW: 12, defaultH: 3,
    restrictedToEmails: ['ryan@rhinoshieldflorida.com', 'gabe@rhinoshieldflorida.com', 'rmarch@rhinoshield.com']
  },
  features_tracker: {
    component: FeaturesTrackerSection,
    title: "Features Tracker",
    description: 'Track feature development progress and status',
    category: 'governance',
    minW: 6, minH: 3, maxH: 5,
    defaultW: 12, defaultH: 3,
    restrictedToEmails: ['ryan@rhinoshieldflorida.com', 'gabe@rhinoshieldflorida.com', 'rmarch@rhinoshield.com']
  },
  targets_pacing: {
    component: TargetsPacingWidgetSection,
    title: "Targets & Pacing",
    description: 'Marketing targets, pacing metrics, and revenue projections from Cometly + Builder Prime',
    category: 'marketing',
    minW: 6, minH: 3, maxH: 5,
    defaultW: 12, defaultH: 3
  }
};

// ==================== SECTION COMPONENTS ====================

// Targets & Pacing Widget Section (Marketing)
function TargetsPacingWidgetSection({ minimized, user }) {
  const [pacingData, setPacingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('mtd');
  const [selectedMarket, setSelectedMarket] = useState('all');
  const [markets, setMarkets] = useState([]);
  
  // Fetch pacing data when period or market changes
  useEffect(() => {
    const fetchPacing = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ period: selectedPeriod });
        if (selectedMarket && selectedMarket !== 'all') {
          params.append('market_id', selectedMarket);
        }
        const response = await axios.get(`${API}/marketing/pacing/realtime?${params.toString()}`);
        setPacingData(response.data);
      } catch (err) {
        console.error('Error fetching pacing data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPacing();
  }, [selectedPeriod, selectedMarket]);
  
  // Fetch markets on mount
  useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const response = await axios.get(`${API}/marketing/pacing/filters`);
        setMarkets(response.data.markets || []);
      } catch (err) {
        console.error('Error fetching markets:', err);
      }
    };
    fetchMarkets();
  }, []);
  
  const handlePeriodChange = (newPeriod) => {
    setSelectedPeriod(newPeriod);
  };
  
  const handleMarketChange = (newMarket) => {
    setSelectedMarket(newMarket);
  };
  
  if (minimized) {
    const revenue = pacingData?.metrics?.revenue;
    return (
      <div className="text-sm text-muted-foreground">
        Revenue: {revenue?.actual ? `$${revenue.actual.toLocaleString()}` : 'Loading...'}
      </div>
    );
  }
  
  return (
    <div className="h-full overflow-auto p-2">
      <TargetsPacingSection
        pacingData={pacingData}
        loading={loading}
        user={user}
        onTargetsUpdated={() => {
          // Refetch data after targets update
          const params = new URLSearchParams({ period: selectedPeriod });
          if (selectedMarket && selectedMarket !== 'all') {
            params.append('market_id', selectedMarket);
          }
          axios.get(`${API}/marketing/pacing/realtime?${params.toString()}`)
            .then(res => setPacingData(res.data))
            .catch(err => console.error('Error refetching:', err));
        }}
        showViewFullButton={true}
        selectedPeriod={selectedPeriod}
        onPeriodChange={handlePeriodChange}
        selectedMarket={selectedMarket}
        onMarketChange={handleMarketChange}
        markets={markets}
      />
    </div>
  );
}

// Admin User Activity Section
function AdminUserActivitySection({ minimized }) {
  return <AdminUserActivityWidget minimized={minimized} />;
}

// Admin Chat Section  
function AdminChatSection({ minimized }) {
  return <AdminChatWidget minimized={minimized} />;
}

function WelcomeBannerSection({ minimized, welcomeConfig }) {
  if (minimized) return null;
  
  return (
    <div className="h-full bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg px-6 py-4 flex items-center justify-between">
      <div>
        <h3 className="text-xl font-bold">{welcomeConfig?.title || 'Welcome to RHINO-1'}</h3>
        <p className="text-blue-100 text-sm">
          {welcomeConfig?.description || 'Your mission control for operations, performance, and results.'}
        </p>
      </div>
    </div>
  );
}

function KeyMetricsSection({ minimized, stats }) {
  if (minimized) return null;
  
  const kpis = stats?.kpis || [
    { label: 'My Jobs', value: '0', trend: 'neutral' },
    { label: 'In Progress', value: '0', trend: 'neutral' },
    { label: 'Completed', value: '0', trend: 'neutral' },
    { label: 'Completion Rate', value: '0%', trend: 'neutral' }
  ];
  
  return (
    <div className="h-full grid grid-cols-2 lg:grid-cols-4 gap-3 p-2">
      {kpis.map((kpi, i) => (
        <div key={i} className="bg-accent/50 rounded-lg p-3 flex flex-col justify-center">
          <p className="text-xs text-muted-foreground">{kpi.label}</p>
          <p className="text-xl font-bold">{kpi.value}</p>
          {kpi.change && <p className="text-xs text-muted-foreground">{kpi.change}</p>}
        </div>
      ))}
    </div>
  );
}

function UpcomingEventsSection({ minimized }) {
  if (minimized) return null;
  
  return (
    <div className="h-full overflow-hidden">
      <DashboardCalendarWidget embedded />
    </div>
  );
}

function RecentActivitySection({ minimized, stats }) {
  if (minimized) return null;
  
  const activities = stats?.recent_activity || [
    { type: 'login', message: 'You logged in', time: 'Just now' }
  ];
  
  return (
    <div className="h-full overflow-auto p-2 space-y-2">
      {activities.map((activity, i) => (
        <div key={i} className="flex items-start gap-2 p-2 rounded-lg hover:bg-accent/50 transition-colors">
          <div className="w-2 h-2 rounded-full bg-primary mt-2" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{activity.message}</p>
            <p className="text-xs text-muted-foreground">{activity.time}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function WhatsComingOnlineSection({ minimized }) {
  if (minimized) return null;
  
  return (
    <div className="h-full overflow-hidden">
      <WhatsComingOnlinePanel embedded />
    </div>
  );
}

function FivePillarsSection({ minimized, user }) {
  if (minimized) return null;
  
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  
  return (
    <div className="h-full overflow-auto p-3">
      <FivePillarsWidget isAdmin={isAdmin} compact={true} embedded={true} />
    </div>
  );
}

function FivePillarsOverviewSection({ minimized, user }) {
  return <FivePillarsOverviewWidget minimized={minimized} embedded={true} />;
}

function DevelopmentPillarsSection({ minimized, user }) {
  if (minimized) return null;
  
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  
  return (
    <div className="h-full overflow-auto p-3">
      <FivePillarsWidget isAdmin={isAdmin} compact={true} embedded={true} showOnly="pillars" />
    </div>
  );
}

function FeaturesTrackerSection({ minimized, user }) {
  if (minimized) return null;
  
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  
  return (
    <div className="h-full overflow-auto p-3">
      <FivePillarsWidget isAdmin={isAdmin} compact={true} embedded={true} showOnly="features" />
    </div>
  );
}

// ==================== SECTION WRAPPER ====================

function DashboardSection({ 
  sectionId, 
  title, 
  minimized, 
  onMinimize, 
  onRemove,
  stats,
  welcomeConfig,
  isDragging,
  user
}) {
  const section = SECTION_REGISTRY[sectionId];
  const SectionComponent = section?.component;
  
  if (!SectionComponent) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        Unknown section: {sectionId}
      </div>
    );
  }
  
  // When minimized: use h-auto to shrink to header only, otherwise h-full
  const cardHeightClass = minimized ? 'h-auto' : 'h-full';
  
  return (
    <Card 
      className={`${cardHeightClass} flex flex-col overflow-hidden transition-all ${isDragging ? 'shadow-2xl ring-2 ring-primary' : ''} ${minimized ? 'bg-muted/30' : ''}`}
      data-testid={`dashboard-widget-${sectionId}`}
    >
      {/* Section Header - Drag Handle */}
      <div 
        className="section-drag-handle flex items-center justify-between px-3 py-2 border-b bg-muted/30 cursor-grab active:cursor-grabbing flex-shrink-0"
      >
        <div className="flex items-center gap-2">
          <GripHorizontal className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{title}</span>
          {minimized && <Badge variant="outline" className="text-[10px]">Minimized</Badge>}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => { e.stopPropagation(); onMinimize(); }}
            title={minimized ? 'Expand' : 'Minimize'}
            data-testid={`widget-minimize-${sectionId}`}
          >
            {minimized ? <Maximize2 className="h-3 w-3" /> : <Minimize2 className="h-3 w-3" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            title="Remove section"
            data-testid={`widget-remove-${sectionId}`}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
      
      {/* Section Content - Hidden when minimized */}
      {!minimized && (
        <div className="flex-1 overflow-hidden min-h-0">
          <SectionComponent minimized={minimized} stats={stats} welcomeConfig={welcomeConfig} user={user} />
        </div>
      )}
    </Card>
  );
}

// ==================== SAVE MODAL ====================

function SaveDashboardModal({ open, onClose, onSave, initialName, saving }) {
  const [name, setName] = useState('');
  const [showAsTab, setShowAsTab] = useState(true);
  
  // Reset form when modal opens - using key pattern instead
  const modalKey = open ? 'open' : 'closed';
  
  const handleSave = () => {
    const dashboardName = name.trim() || initialName || 'My Dashboard';
    onSave({ name: dashboardName, showAsTab });
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose} key={modalKey}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save Dashboard</DialogTitle>
          <DialogDescription>
            Save your current dashboard layout
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="dashboard-name">Dashboard Name *</Label>
            <Input
              id="dashboard-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={initialName || "My Dashboard"}
              className="mt-1"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Checkbox
              id="show-as-tab"
              checked={showAsTab}
              onCheckedChange={setShowAsTab}
            />
            <Label htmlFor="show-as-tab" className="cursor-pointer">
              Show as tab at top
            </Label>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Dashboard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== UNSAVED CHANGES MODAL ====================

function UnsavedChangesModal({ open, onSave, onDiscard, onCancel }) {
  return (
    <AlertDialog open={open} onOpenChange={onCancel}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            Unsaved Changes
          </AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes to your dashboard layout. What would you like to do?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <Button variant="outline" onClick={onDiscard}>Discard Changes</Button>
          <AlertDialogAction onClick={onSave}>Save & Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ==================== REMOVE SECTION CONFIRM ====================

function RemoveSectionModal({ open, sectionTitle, onConfirm, onCancel }) {
  return (
    <AlertDialog open={open} onOpenChange={onCancel}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Section?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove &quot;{sectionTitle}&quot; from this dashboard?
            You can restore it by resetting to default.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ==================== ADD WIDGET MODAL ====================

function AddWidgetModal({ open, onClose, onAdd, currentWidgets, userRole, userEmail }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Check if user is admin
  const isAdmin = userRole === 'super_admin' || userRole === 'admin';
  
  // Get available widgets (not already in dashboard, filtered by admin access and email restrictions)
  const availableWidgets = Object.entries(SECTION_REGISTRY).filter(
    ([id, config]) => {
      // Skip if already in dashboard
      if (currentWidgets.includes(id)) return false;
      // Skip admin-only widgets for non-admins
      if (config.adminOnly && !isAdmin) return false;
      // Skip widgets restricted to specific emails
      if (config.restrictedToEmails && !config.restrictedToEmails.includes(userEmail?.toLowerCase())) return false;
      return true;
    }
  );
  
  // Filter by category
  const filteredWidgets = selectedCategory === 'all' 
    ? availableWidgets 
    : availableWidgets.filter(([, config]) => config.category === selectedCategory);
  
  // Get unique categories from available widgets
  const categories = ['all', ...new Set(availableWidgets.map(([, config]) => config.category))];
  
  const handleAdd = (widgetId) => {
    const widget = SECTION_REGISTRY[widgetId];
    onAdd({
      section_id: widgetId,
      x: 0,
      y: Infinity, // Add to bottom
      w: widget.defaultW || 6,
      h: widget.defaultH || 3,
      minimized: false
    });
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            Add Widget
          </DialogTitle>
          <DialogDescription>
            Choose a widget to add to your dashboard
          </DialogDescription>
        </DialogHeader>
        
        {/* Category Filter */}
        <div className="flex gap-2 flex-wrap py-2 border-b">
          {categories.map(cat => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat)}
              className="capitalize"
            >
              {cat === 'all' ? '📋 All' : `${WIDGET_CATEGORIES[cat]?.icon || '📌'} ${WIDGET_CATEGORIES[cat]?.label || cat}`}
            </Button>
          ))}
        </div>
        
        {/* Widget Grid */}
        <div className="flex-1 overflow-y-auto py-4">
          {filteredWidgets.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">All widgets from this category are already added</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {filteredWidgets.map(([id, config]) => (
                <Card 
                  key={id} 
                  className="cursor-pointer hover:border-primary hover:shadow-md transition-all group"
                  onClick={() => handleAdd(id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-sm group-hover:text-primary transition-colors">
                        {config.title}
                      </h4>
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {WIDGET_CATEGORIES[config.category]?.icon} {config.category}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {config.description || 'No description'}
                    </p>
                    <div className="flex items-center gap-2 mt-3 text-[10px] text-muted-foreground">
                      <span>Min: {config.minW}x{config.minH}</span>
                      <span>•</span>
                      <span>Default: {config.defaultW}x{config.defaultH}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        
        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== MAIN DASHBOARD BUILDER ====================

export default function DashboardBuilder() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Container ref for responsive width
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800); // Default reasonable width
  
  // Dashboard state
  const [dashboards, setDashboards] = useState({ system_default: null, user_dashboards: [] });
  const [activeDashboard, setActiveDashboard] = useState(null);
  const [layout, setLayout] = useState([]);
  const [sectionStates, setSectionStates] = useState({}); // { section_id: { minimized: bool } }
  const [welcomeConfig, setWelcomeConfig] = useState(null);
  
  // Dirty state tracking
  const [isDirty, setIsDirty] = useState(false);
  const [originalLayout, setOriginalLayout] = useState(null);
  
  // Modals
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const [removeSectionModal, setRemoveSectionModal] = useState({ open: false, sectionId: null, title: '' });
  const [addWidgetModalOpen, setAddWidgetModalOpen] = useState(false);
  const [pendingTabSwitch, setPendingTabSwitch] = useState(null);
  
  // Grid config
  const GRID_COLS = 12;
  const ROW_HEIGHT = 80;
  const MINIMIZED_HEIGHT = 1; // 1 row = 80px for minimized sections
  
  // Computed layout with minimized heights applied and restricted widgets filtered out
  const computedLayout = useMemo(() => {
    return layout
      .filter(item => {
        const section = SECTION_REGISTRY[item.i];
        // Filter out widgets restricted to specific emails if user doesn't have access
        if (section?.restrictedToEmails && !section.restrictedToEmails.includes(user?.email?.toLowerCase())) {
          return false;
        }
        return true;
      })
      .map(item => {
        if (sectionStates[item.i]?.minimized) {
          return { ...item, h: MINIMIZED_HEIGHT, minH: MINIMIZED_HEIGHT, maxH: MINIMIZED_HEIGHT };
        }
        const section = SECTION_REGISTRY[item.i];
        return { ...item, minH: section?.minH || 2, maxH: section?.maxH || 8 };
      });
  }, [layout, sectionStates, user?.email]);
  
  // Measure container width for responsive grid
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        // Use container width, with a minimum fallback
        setContainerWidth(Math.max(width, 300));
      }
    };
    
    // Initial measurement
    updateWidth();
    
    // Also measure after a short delay for layout settling
    const timeoutId = setTimeout(updateWidth, 50);
    
    window.addEventListener('resize', updateWidth);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWidth);
    };
  }, [loading]); // Re-measure when loading completes
  
  const fetchData = useCallback(async () => {
    try {
      const [dashboardsRes, statsRes, welcomeRes] = await Promise.all([
        axios.get(`${API}/dashboards/`),
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/platform/welcome-config`)
      ]);
      
      setWelcomeConfig(welcomeRes.data);
      
      setDashboards(dashboardsRes.data);
      setStats(statsRes.data);
      
      // Set active dashboard
      const userDefault = dashboardsRes.data.user_dashboards.find(d => d.is_default);
      const active = userDefault || dashboardsRes.data.system_default;
      
      if (active) {
        setActiveDashboard(active);
        initializeLayout(active.layout);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch dashboards and stats on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const initializeLayout = (dashboardLayout) => {
    // Widget ID migration map - old IDs to new IDs
    const WIDGET_MIGRATIONS = {
      'five_pillars': 'five_pillars_overview'  // Old governance widget replaced with new overview widget
    };
    
    // Migrate old widget IDs to new ones
    const migratedLayout = dashboardLayout.map(item => {
      const newSectionId = WIDGET_MIGRATIONS[item.section_id] || item.section_id;
      return { ...item, section_id: newSectionId };
    });
    
    // Filter out widgets that don't exist in the registry (unknown sections)
    const validLayout = migratedLayout.filter(item => {
      if (!SECTION_REGISTRY[item.section_id]) {
        console.warn(`Removing unknown widget: ${item.section_id}`);
        return false;
      }
      return true;
    });
    
    // Convert dashboard layout to react-grid-layout format
    const gridLayout = validLayout.map(item => ({
      i: item.section_id,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.minimized ? 1 : item.h,
      minW: SECTION_REGISTRY[item.section_id]?.minW || 3,
      minH: 1,
      maxH: SECTION_REGISTRY[item.section_id]?.maxH || 6
    }));
    
    // Initialize section states
    const states = {};
    validLayout.forEach(item => {
      states[item.section_id] = { minimized: item.minimized || false };
    });
    
    setLayout(gridLayout);
    setSectionStates(states);
    setOriginalLayout(JSON.stringify(validLayout));
    setIsDirty(false);
  };
  
  // Handle layout change from react-grid-layout
  const handleLayoutChange = useCallback((newLayout) => {
    // Preserve height for minimized sections (should stay at 1)
    const adjustedLayout = newLayout.map(item => {
      if (sectionStates[item.i]?.minimized) {
        return { ...item, h: 1, minH: 1 };
      }
      return item;
    });
    
    setLayout(adjustedLayout);
    
    // Check if layout actually changed from original
    // Sort both arrays by section_id for consistent comparison
    const currentLayoutData = adjustedLayout
      .map(item => ({
        section_id: item.i,
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
        minimized: sectionStates[item.i]?.minimized || false
      }))
      .sort((a, b) => a.section_id.localeCompare(b.section_id));
    
    const originalLayoutData = originalLayout 
      ? JSON.parse(originalLayout).sort((a, b) => a.section_id.localeCompare(b.section_id))
      : [];
    
    // Compare the sorted layouts
    const layoutsMatch = JSON.stringify(currentLayoutData) === JSON.stringify(originalLayoutData);
    setIsDirty(!layoutsMatch);
  }, [sectionStates, originalLayout]);
  
  // Handle minimize toggle
  const handleMinimize = (sectionId) => {
    const isCurrentlyMinimized = sectionStates[sectionId]?.minimized;
    const currentItem = layout.find(l => l.i === sectionId);
    const section = SECTION_REGISTRY[sectionId];
    
    // Update section states with original height stored when minimizing
    setSectionStates(prev => ({
      ...prev,
      [sectionId]: { 
        ...prev[sectionId], 
        minimized: !isCurrentlyMinimized,
        originalHeight: !isCurrentlyMinimized ? currentItem?.h : prev[sectionId]?.originalHeight
      }
    }));
    
    // Update base layout height for when it's expanded again
    if (isCurrentlyMinimized) {
      // Restoring from minimized - set height to original or default
      const originalH = sectionStates[sectionId]?.originalHeight || section?.defaultH || 3;
      setLayout(prev => prev.map(item => 
        item.i === sectionId ? { ...item, h: originalH } : item
      ));
    }
    
    setIsDirty(true);
  };
  
  // Handle remove section
  const handleRemoveSection = (sectionId) => {
    const section = SECTION_REGISTRY[sectionId];
    setRemoveSectionModal({
      open: true,
      sectionId,
      title: section?.title || sectionId
    });
  };
  
  const confirmRemoveSection = () => {
    const sectionId = removeSectionModal.sectionId;
    setLayout(prev => prev.filter(item => item.i !== sectionId));
    setSectionStates(prev => {
      const newStates = { ...prev };
      delete newStates[sectionId];
      return newStates;
    });
    setRemoveSectionModal({ open: false, sectionId: null, title: '' });
    setIsDirty(true);
    toast.success('Section removed');
  };
  
  // Get current layout data for saving
  const getCurrentLayoutData = () => {
    return layout.map(item => ({
      section_id: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
      minimized: sectionStates[item.i]?.minimized || false
    }));
  };
  
  // Add widget to dashboard
  const handleAddWidget = (widgetConfig) => {
    const { section_id, x, y, w, h, minimized } = widgetConfig;
    const config = SECTION_REGISTRY[section_id];
    
    // Calculate y position (add to bottom)
    const maxY = layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
    
    const newItem = {
      i: section_id,
      x: x || 0,
      y: maxY,
      w: w || config?.defaultW || 6,
      h: h || config?.defaultH || 3,
      minW: config?.minW || 3,
      minH: config?.minH || 2,
      maxH: config?.maxH || 8
    };
    
    setLayout(prev => [...prev, newItem]);
    setSectionStates(prev => ({ ...prev, [section_id]: { minimized: minimized || false } }));
    setIsDirty(true);
    toast.success(`Added "${config?.title || section_id}" widget`);
  };
  
  // Get list of current widget IDs
  const currentWidgetIds = useMemo(() => layout.map(item => item.i), [layout]);
  
  // Save dashboard
  const handleSave = async ({ name, showAsTab }) => {
    setSaving(true);
    try {
      const layoutData = getCurrentLayoutData();
      
      if (activeDashboard?.owner_type === 'user') {
        // Update existing user dashboard
        await axios.put(`${API}/dashboards/${activeDashboard.id}`, {
          name,
          show_as_tab: showAsTab,
          layout: layoutData
        });
        toast.success('Dashboard saved');
      } else {
        // Create new user dashboard
        const response = await axios.post(`${API}/dashboards/`, {
          name,
          show_as_tab: showAsTab,
          layout: layoutData
        });
        
        // Set as default
        await axios.post(`${API}/dashboards/${response.data.id}/set-default`);
        toast.success('Dashboard created');
      }
      
      // Refresh data
      await fetchData();
      setSaveModalOpen(false);
      setIsDirty(false);
      
      // Handle pending tab switch
      if (pendingTabSwitch) {
        switchToTab(pendingTabSwitch);
        setPendingTabSwitch(null);
      }
    } catch (error) {
      console.error('Failed to save dashboard:', error);
      toast.error('Failed to save dashboard');
    } finally {
      setSaving(false);
    }
  };
  
  // Reset to default
  const handleResetToDefault = async () => {
    try {
      const response = await axios.post(`${API}/dashboards/reset-to-default`);
      toast.success('Dashboard reset to default');
      await fetchData();
    } catch (error) {
      console.error('Failed to reset dashboard:', error);
      toast.error('Failed to reset dashboard');
    }
  };
  
  // Tab switching with unsaved changes check
  const handleTabClick = (dashboard) => {
    if (isDirty) {
      setPendingTabSwitch(dashboard);
      setUnsavedModalOpen(true);
    } else {
      switchToTab(dashboard);
    }
  };
  
  const switchToTab = (dashboard) => {
    setActiveDashboard(dashboard);
    initializeLayout(dashboard.layout);
    setUnsavedModalOpen(false);
  };
  
  const handleUnsavedDiscard = () => {
    if (pendingTabSwitch) {
      switchToTab(pendingTabSwitch);
      setPendingTabSwitch(null);
    }
    setIsDirty(false);
  };
  
  const handleUnsavedSave = () => {
    setUnsavedModalOpen(false);
    setSaveModalOpen(true);
  };
  
  // Get tabs to display
  const tabs = useMemo(() => {
    return dashboards.user_dashboards.filter(d => d.show_as_tab);
  }, [dashboards.user_dashboards]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4" ref={containerRef}>
      {/* Header with tabs and actions */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        {/* Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <Button
              key={tab.id}
              variant={activeDashboard?.id === tab.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleTabClick(tab)}
              className="whitespace-nowrap"
            >
              {tab.name}
            </Button>
          ))}
          {tabs.length === 0 && (
            <span className="text-sm text-muted-foreground">No saved dashboards yet</span>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {isDirty && (
            <Badge variant="outline" className="text-amber-600 border-amber-600">
              <AlertCircle className="h-3 w-3 mr-1" />
              Unsaved changes
            </Badge>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetToDefault}
            title="Reset to default layout"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAddWidgetModalOpen(true)}
            className="border-dashed border-primary/50 text-primary hover:bg-primary/10"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Widget
          </Button>
          
          <Button
            size="sm"
            onClick={() => setSaveModalOpen(true)}
            disabled={!isDirty && activeDashboard?.owner_type === 'user'}
          >
            <Save className="h-4 w-4 mr-1" />
            Save Dashboard
          </Button>
        </div>
      </div>
      
      {/* Grid Layout - Container invisible but functional */}
      <div className="min-h-[600px] overflow-hidden w-full">
        <GridLayout
          className="layout"
          layout={computedLayout}
          cols={GRID_COLS}
          rowHeight={ROW_HEIGHT}
          width={containerWidth}
          onLayoutChange={handleLayoutChange}
          draggableHandle=".section-drag-handle"
          compactType="vertical"
          preventCollision={false}
          isResizable={true}
          resizeHandles={['se']}
          margin={[12, 12]}
        >
          {computedLayout.map(item => {
            const isMinimized = sectionStates[item.i]?.minimized || false;
            return (
              <div 
                key={item.i} 
                className={isMinimized ? 'overflow-hidden' : ''}
                style={isMinimized ? { maxHeight: `${ROW_HEIGHT}px` } : {}}
              >
                <DashboardSection
                  sectionId={item.i}
                  title={SECTION_REGISTRY[item.i]?.title || item.i}
                  minimized={isMinimized}
                  onMinimize={() => handleMinimize(item.i)}
                  onRemove={() => handleRemoveSection(item.i)}
                  stats={stats}
                  welcomeConfig={welcomeConfig}
                  user={user}
                />
              </div>
            );
          })}
        </GridLayout>
      </div>
      
      {/* Modals */}
      <SaveDashboardModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onSave={handleSave}
        initialName={activeDashboard?.name}
        saving={saving}
      />
      
      <UnsavedChangesModal
        open={unsavedModalOpen}
        onSave={handleUnsavedSave}
        onDiscard={handleUnsavedDiscard}
        onCancel={() => { setUnsavedModalOpen(false); setPendingTabSwitch(null); }}
      />
      
      <RemoveSectionModal
        open={removeSectionModal.open}
        sectionTitle={removeSectionModal.title}
        onConfirm={confirmRemoveSection}
        onCancel={() => setRemoveSectionModal({ open: false, sectionId: null, title: '' })}
      />
      
      <AddWidgetModal
        open={addWidgetModalOpen}
        onClose={() => setAddWidgetModalOpen(false)}
        onAdd={handleAddWidget}
        currentWidgets={currentWidgetIds}
        userRole={user?.role}
        userEmail={user?.email}
      />
    </div>
  );
}
